import React from 'react';
import { AlertTriangle, X, Plus, RotateCcw } from 'lucide-react';

interface IngredienteSimilar {
  id: number;
  nombre: string;
  categoria: string;
  stock_actual?: number;
  activo: boolean;
  unidad_medida?: {
    abreviacion: string;
  };
}

interface IngredienteSimilarModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  ingredientesSimilares: IngredienteSimilar[];
  isExactoInactivo?: boolean;
  onForzarCreacion: () => void;
  onReactivarYAgregar: (ingrediente: IngredienteSimilar) => void;
}

const IngredienteSimilarModal: React.FC<IngredienteSimilarModalProps> = ({
  isOpen,
  onClose,
  message,
  ingredientesSimilares,
  isExactoInactivo = false,
  onForzarCreacion,
  onReactivarYAgregar,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isExactoInactivo
                ? 'Ingrediente Inactivo Encontrado'
                : 'Ingredientes Similares Encontrados'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
            {message}
          </p>

          {ingredientesSimilares.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                {isExactoInactivo
                  ? 'Ingrediente encontrado:'
                  : 'Ingredientes similares encontrados:'}
              </p>

              <div className="space-y-3">
                {ingredientesSimilares.map((ingrediente) => (
                  <div
                    key={ingrediente.id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {ingrediente.nombre}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Categoría: {ingrediente.categoria}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Stock: {ingrediente.stock_actual || 0}{' '}
                          {ingrediente.unidad_medida?.abreviacion || 'ud'}
                        </p>
                        <p
                          className={`text-sm font-medium ${
                            ingrediente.activo
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          Estado: {ingrediente.activo ? 'Activo' : 'Inactivo'}
                        </p>
                      </div>

                      {!ingrediente.activo && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onReactivarYAgregar(ingrediente)}
                            className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors duration-200"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Usar este ingrediente
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Recomendación:</strong>{' '}
                  {isExactoInactivo
                    ? 'Te sugerimos usar el ingrediente existente para mantener el historial y evitar duplicados.'
                    : 'Revisa si alguno de estos ingredientes es el que necesitas antes de crear uno nuevo.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
          >
            Cancelar
          </button>

          <button
            onClick={onForzarCreacion}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear Nuevo de Todas Formas
          </button>
        </div>
      </div>
    </div>
  );
};

export default IngredienteSimilarModal;
